import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

import {
  REPORT_CATEGORIES,
  formatDiagnosticsText,
  type DiagnosticsShotEntry,
  type ReportCategory,
} from '@/lib/diagnostics/diagnosticsReport';
import { collectDiagnostics } from '@/lib/diagnostics/collectDiagnostics';
import type { Annotation } from '@/lib/diagnostics/annotationModel';
import {
  captureScreen,
  prepareShotFromFile,
  renderAnnotatedShot,
  type CapturedShot,
} from '@/lib/diagnostics/screenshotCapture';
import { ScreenshotAnnotator } from './ScreenshotAnnotator';
import { useEscapeLayer } from '@/components/escapeLayer';
import { HaiIcon } from '@/components/icons';
import { services } from '@/services';
import { FEEDBACK_ATTACHMENTS_SUPPORTED, SUPPORT_REPORT_SUPPORTED } from '@/services/supportService';
import {
  ATTACHMENT_MAX_COUNT,
  attachmentsWithinLimits,
  describeSubmitFailure,
  shotToFile,
  type SubmitFailure,
} from '@/lib/diagnostics/feedbackSubmission';
import { debugLog } from '@/lib/debugLog';
import { ApiHttpError } from '@/types';

/**
 * ReportIssueDialog — 一顆按鈕交出可查的診斷(R249 Step 1)
 * =============================================================================
 *
 * ## 設計要點
 *
 * - **敘述與分類全部選填**。打開就已經有完整診斷了(API 足跡 / 錯誤 / build / 路由),
 *   文字只是加分。外部使用者不填也拿得到可查的東西,這是整個設計的前提。
 * - **「看看會送出什麼」可展開**,而且展開的**就是**複製出去的那個字串 —— 同一份
 *   `formatDiagnosticsText` 輸出,不是另做一份摘要。這個功能會被貼進群組,
 *   「看到的 == 交出去的」是它能不能被信任的唯一保證。
 * - **不用 `useToast`**:本元件也會被 [AppErrorBoundary](../app/AppErrorBoundary.tsx)
 *   render,而 boundary 在 `ToastProvider` **外圈** —— `useToast()` 在那裡會 throw
 *   (它沒有 context 就丟錯)。複製成功的回饋改成按鈕自己的短暫狀態。
 *   同理不用 store:白畫面時 React tree 已崩,能少依賴一個就少一個。
 * - 關閉三徑(CIS):Esc / 遮罩 / ✕。
 *
 * ## 截圖:原生螢幕擷取(R257.1)
 *
 * DOM 重繪那條已整條移除(原理上節點越多越慢,畫布與成效報表拍不出來)。現在用
 * `getDisplayMedia` 直接取螢幕像素 —— 速度與畫面複雜度無關,跨域素材縮圖也拍得到。
 * 貼上 / 選檔保留為備援(企業環境停用螢幕分享、或瀏覽器不支援時仍有路)。
 *
 * **最多 3 張(R274;回報單 FB-20260820-0008)** —— 上限與 BE `MAX_ATTACHMENTS` 同值;
 * 三個入口(擷取 / 選檔〔`multiple`〕/ 貼上)共用同一個 `addShot`,**每張各自帶自己的
 * 圈選框**(框的座標只對它那張圖有意義)。滿了把新增鈕收起來並把原因寫在畫面上。
 *
 * ⚠️ **擷取前必須把整個回報 UI 藏起來** —— 注意是**最外層的遮罩**,不是只有對話框本體:
 * `--scrim` 是 82% 不透明的深色,只藏內容的話擷取到的畫面會整片壓暗(R257.1 第一版就是
 * 這樣,實測「特別黑」)。用 ref 直接改 style 而不是 setState:`getDisplayMedia` 要求
 * **使用者手勢**,中間插一次 React 重繪有失去 gesture 的風險;擷取那一行也不能被別的
 * `await` 隔開。
 *
 * ## 送出 vs 複製
 *
 * 「複製診斷碼」永遠在,而且失敗時是退路(回報功能自己可能被同一個故障擋住)。
 * 送出鈕由 `SUPPORT_REPORT_SUPPORTED` gate —— BE 端點同步開發中,翻一個常數即通。
 *
 * ⚠️ 「預覽 == 複製」那條保證的範圍是**文字**:預覽區顯示的與複製出去的是同一個字串。
 * 送出的 payload 是「同一份資料 + 截圖 base64」—— base64 刻意不進文字(十萬字元貼不進
 * 聊天室,而且等於把畫面內容散到使用者沒預期的地方)。
 */

export interface ReportIssueDialogProps {
  onClose: () => void;
  /** 由呼叫端預填分類(例:錯誤 toast 的「回報」帶 `ui_bug`)。 */
  initialCategory?: ReportCategory | null;
}

/** 一張待送出的截圖 + 它自己的圈選框(FB-20260820-0008)。 */
interface ShotDraft {
  shot: CapturedShot;
  annotations: readonly Annotation[];
}

export function ReportIssueDialog({ onClose, initialCategory = null }: ReportIssueDialogProps) {
  const { t } = useTranslation('common');
  const [category, setCategory] = useState<ReportCategory | null>(initialCategory);
  const [note, setNote] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [copyState, setCopyState] = useState<'idle' | 'done' | 'failed'>('idle');
  /**
   * 附上的截圖(FB-20260820-0008,海易吳承暐:「希望回報問題可以附上多張截圖
   * (怕太多可以限制最多三張)」)。上限 `ATTACHMENT_MAX_COUNT` = 3,那正好是 BE
   * `MAX_ATTACHMENTS` 的值 —— 兩邊同一把尺,不是我們自己挑的數字。
   *
   * 🔴 **框跟著圖走**(`annotations` 在每一筆裡面,不是共用一份):一張圖上的
   * `rect(412,308,180,44)` 換到另一張圖上會指到完全不相干的地方,對錯位置比沒有更糟。
   */
  const [shots, setShots] = useState<readonly ShotDraft[]>([]);
  const [shotState, setShotState] = useState<'idle' | 'failed' | 'unsupported'>('idle');
  const shotsFull = shots.length >= ATTACHMENT_MAX_COUNT;
  /**
   * 🔴 綁在**最外層(遮罩)**而不是對話框本體。
   *
   * R257.1 第一版綁在 `role="dialog"` 那層,結果擷取到的畫面「特別黑」—— 因為 `--scrim`
   * 是 82% 不透明的深色遮罩,它是對話框的**父容器**:藏了對話框、遮罩還蓋在整個畫面上。
   * 擷取時要讓**整個回報 UI 都消失**,不只是它的內容。
   */
  const overlayRef = useRef<HTMLDivElement>(null);
  const [submitState, setSubmitState] = useState<'idle' | 'sending' | 'sent' | 'failed'>('idle');
  const [sentId, setSentId] = useState<string | null>(null);
  /** 部分成功:文字存了但有幾張圖沒上傳成功(docs/94 §2.5)。 */
  const [failedAttachments, setFailedAttachments] = useState(0);
  const [submitFailure, setSubmitFailure] = useState<SubmitFailure | null>(null);
  const preRef = useRef<HTMLPreElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // R393 — ESC 走全站分層(CIS:第二層視窗開著時 ESC 只關最上層)。之前是 window 層
  // 自己聽 keydown:回報對話框開在燈箱上面時,一次 ESC 會連底下的燈箱一起關掉。
  useEscapeLayer(onClose);

  /**
   * 貼上截圖(Cmd/Ctrl + V)。
   *
   * 這是「兩種都要」裡成本最低、成功率最高的一種:使用者本來就習慣先用系統截圖工具框好
   * 範圍再貼 —— 而且系統截圖**沒有跨域 taint 問題**,素材縮圖拍得到,自動截圖拍不到。
   */
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      const file = Array.from(e.clipboardData?.files ?? []).find((f) => f.type.startsWith('image/'));
      if (!file) return;
      e.preventDefault();
      void prepareShotFromFile(file).then((next) => {
        if (!next) {
          setShotState('failed');
          return;
        }
        addShot(next);
      });
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
    // deps 空是刻意的:`addShot` 只讀 setter(identity 穩定)與上限常數,
    // 每次 render 重新註冊 listener 沒有意義。
  }, []);

  /**
   * 快照在**開窗當下**取一次(依 note / category 重算文字,但足跡固定)。
   *
   * ⚠️ 刻意不每次 render 重新讀 buffer:使用者一邊打字、buffer 一邊被背景請求推進,
   * 預覽會跳動,而且「看到的」與最後複製出去的可能不是同一份 —— 那正好破壞上面那條
   * 「看到的 == 交出去的」保證。
   */
  const snapshot = useMemo(() => collectDiagnostics({ category: null, note: '' }), []);
  const text = useMemo(() => {
    // 預覽 / 複製用的截圖摘要。`dataBase64` 給空字串是刻意的 —— 文字段只印尺寸與大小,
    // 真正的 base64 在送出時才由 `renderAnnotatedShot` 產生(還要把標註框燒進去)。
    // ⚠️ 這個陣列必須在 callback **內**建立:放在外面每次 render 都是新 identity,
    // useMemo 的 deps 每次都變 = 快取完全失效(而預覽文字每次重算會讓 <pre> 閃動)。
    const shotSummaries: DiagnosticsShotEntry[] = shots.map((d) => ({
      shot: {
        mime: 'image/webp',
        dataBase64: '',
        width: d.shot.width,
        height: d.shot.height,
        bytes: d.shot.bytes,
      },
      annotations: d.annotations,
    }));
    return formatDiagnosticsText({
      ...snapshot,
      category,
      note: note.trim(),
      shots: shotSummaries,
    });
  }, [snapshot, category, note, shots]);

  /**
   * 收一張截圖。滿 3 張就不收(而且 UI 那時已經不給按 —— 這裡是雙保險:貼上是全域
   * 監聽,鈕藏起來也還按得到 Cmd+V)。
   */
  function addShot(next: CapturedShot): void {
    setShots((prev) => (prev.length >= ATTACHMENT_MAX_COUNT ? prev : [...prev, { shot: next, annotations: [] }]));
    setShotState('idle');
  }

  function removeShotAt(index: number): void {
    setShots((prev) => prev.filter((_, i) => i !== index));
  }

  function setAnnotationsAt(index: number, next: readonly Annotation[]): void {
    setShots((prev) => prev.map((d, i) => (i === index ? { ...d, annotations: next } : d)));
  }

  /**
   * 送出成功之後,那顆鈕該複製的是**編號**而不是診斷碼(Bryan 2026-08-19 回報:
   * 「複製的不是編號,而是一長串東西」)。
   *
   * 兩個階段要的東西不同:**送出前**手上只有診斷碼,那是「送不出去時的退路」;
   * **送出後**回報單已經在後端了,使用者要的是那個能拿來跟我們對話的 `FB-…` 編號 ——
   * 這時再複製一長串診斷貼進聊天室,對雙方都沒有用。
   */
  async function handleCopySerial(): Promise<void> {
    if (!sentId) return;
    try {
      await navigator.clipboard.writeText(sentId);
      setCopyState('done');
      window.setTimeout(() => setCopyState('idle'), 2000);
    } catch {
      setCopyState('failed');
    }
  }

  async function handleCopy(): Promise<void> {
    try {
      // Clipboard API 需要 secure context + 使用者手勢;兩者都成立(按鈕點擊 + https)。
      // 失敗的真實情境是權限被瀏覽器策略拒絕 → 退路是「文字已在畫面上,自己選取」,
      // 所以失敗時強制展開預覽並選取全文,而不是只吐一句錯誤。
      await navigator.clipboard.writeText(text);
      setCopyState('done');
      window.setTimeout(() => setCopyState('idle'), 2000);
    } catch {
      setCopyState('failed');
      setShowPreview(true);
      window.setTimeout(() => {
        const el = preRef.current;
        if (!el) return;
        const range = document.createRange();
        range.selectNodeContents(el);
        const sel = window.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(range);
      }, 0);
    }
  }

  /**
   * 原生螢幕擷取。**同步**隱藏對話框後立刻呼叫 —— 中間不要插 `await`(見檔頭)。
   */
  async function handleCaptureScreen(): Promise<void> {
    const overlay = overlayRef.current;
    if (overlay) overlay.style.visibility = 'hidden';
    try {
      const outcome = await captureScreen();
      if (outcome.kind === 'ok') {
        addShot(outcome.shot);
        return;
      }
      // 使用者按「不分享」不是錯誤,不要吐紅字 —— 他只是改變主意。
      if (outcome.kind === 'cancelled') return;
      setShotState(outcome.kind === 'unsupported' ? 'unsupported' : 'failed');
    } finally {
      if (overlay) overlay.style.visibility = '';
    }
  }

  async function handlePickFiles(files: readonly File[]): Promise<void> {
    // 一次選多張:只收「現在還放得下」的那幾張(而不是收滿再靜默丟掉多的)。
    const room = Math.max(ATTACHMENT_MAX_COUNT - shots.length, 0);
    for (const file of files.slice(0, room)) {
      const next = await prepareShotFromFile(file);
      if (!next) {
        setShotState('failed');
        return;
      }
      addShot(next);
    }
  }

  async function handleSubmit(): Promise<void> {
    setSubmitState('sending');
    try {
      // 送出前才把標註框燒進圖片(編碼耗時,不該在每次拖曳後都跑一遍)。
      // 即使截圖檔案送不出去(WAF gate),摘要與標註仍會經由 description 送達 —— 所以
      // 這一步不是白做的。
      const burned: DiagnosticsShotEntry[] = [];
      for (const d of shots) {
        // 燒不出來(canvas 取不到 context)那張就不附 —— 診斷裡的 `shots` 與實際送出的
        // 檔案永遠是同一份清單,不會出現「診斷說有 3 張、附件只有 2 張」。
        const out = await renderAnnotatedShot(d.shot, d.annotations);
        if (out) burned.push({ shot: out, annotations: d.annotations });
      }
      const report = {
        ...snapshot,
        category,
        note: note.trim(),
        shots: burned,
      };
      // ⏳ 截圖檔案只在 WAF 放行後才附(帶檔案的 multipart 會被 Cloud Armor 擋掉整個請求,
      // 見 supportService 檔頭的實測對照表)。
      // ⚠️ 附的是 `burned`(已燒進標註框)不是原始截圖 —— 見 shotToFile 的 JSDoc。
      // ⚠️ 檔名的序號(`screenshot-N`)= `shots[]` 的順序,診斷裡的框才對得回哪一張。
      const candidates = FEEDBACK_ATTACHMENTS_SUPPORTED
        ? burned.map((e, i) => shotToFile(e.shot, i + 1)).filter((f): f is File => f !== null)
        : [];
      // docs/94 §6 驗收項 6:上限兩邊都要擋。目前 `SHOT_MAX_BYTES`(400 KB)遠低於 BE 的
      // 5 MiB,所以這條實務上不會觸發 —— 但它是**契約鏡射**:哪天調高截圖品質上限,
      // 這裡會先靜默丟掉附件而不是讓整筆回報吃 413(文字比截圖重要)。
      const files = attachmentsWithinLimits(candidates) ? candidates : [];
      if (candidates.length > 0 && files.length === 0) {
        debugLog('ui', '[report] attachment exceeds BE limits — sending text only');
      }
      const res = await services.supportService.submitReport({
        report,
        // 分類必填(BE 422)。使用者沒選就送 `other` —— 不要因此擋住送出,
        // 那會把「一顆按鈕」變成「先回答一個問題」。
        category: category ?? 'other',
        note: note.trim(),
        files,
      });
      // 顯示 **serial**(FB-20260818-0007)不是 report_id:docs/94 §6 驗收項 3,
      // 使用者要拿這個編號跟我們對話,而 ULID 沒有人念得出來。
      setSentId(res.serial || res.reportId);
      setFailedAttachments(res.attachmentFailedCount);
      setSubmitState('sent');
    } catch (e) {
      setSubmitState('failed');
      const failure = describeSubmitFailure(
        e instanceof ApiHttpError ? e.status : 0,
        e instanceof ApiHttpError ? String(e.error?.code ?? '') : null,
      );
      setSubmitFailure(failure);
      // ⚠️ 只有「重試沒用」時才自動複製診斷碼。這條是刻意的:回報功能自己可能正被同一個
      // 故障擋住(401 / WAF / 離線),那時使用者最需要的是手上有東西可以貼給我們。
      // 但限流類的失敗等一分鐘就會成功 —— 自動複製會暗示「送不出去了」,反而讓他放棄送出。
      if (!failure.retryable) {
        try {
          await navigator.clipboard.writeText(text);
          setCopyState('done');
        } catch {
          setShowPreview(true);
        }
      }
    }
  }

  const trailEmpty = snapshot.apiTrail.length === 0;

  return (
    <div ref={overlayRef} style={scrimStyle} data-testid="report-issue-dialog">
      <button type="button" aria-label={t('report.previewHide')} onClick={onClose} style={backdropBtnStyle} />
      <div role="dialog" aria-modal="true" aria-label={t('report.title')} style={modalStyle}>
        <div style={headStyle}>
          <span style={titleStyle}>{t('report.title')}</span>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('actions.close')}
            style={closeBtnStyle}
            data-testid="report-close"
          >
            <HaiIcon name="x" size={14} />
          </button>
        </div>

        <div style={bodyStyle}>
          <p style={introStyle}>{t('report.intro')}</p>

          <div style={fieldStyle}>
            <span style={labelStyle}>{t('report.categoryLabel')}</span>
            <div style={chipRowStyle}>
              {REPORT_CATEGORIES.map((c) => {
                const on = category === c;
                return (
                  <button
                    key={c}
                    type="button"
                    aria-pressed={on}
                    onClick={() => setCategory(on ? null : c)}
                    style={on ? chipOnStyle : chipStyle}
                    data-testid={`report-category-${c}`}
                  >
                    {t(`report.category.${c}` as 'report.category.other')}
                  </button>
                );
              })}
            </div>
          </div>

          <div style={fieldStyle}>
            <label htmlFor="report-note" style={labelStyle}>
              {t('report.noteLabel')}
            </label>
            <textarea
              id="report-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t('report.notePlaceholder')}
              rows={3}
              style={textareaStyle}
              data-testid="report-note"
            />
          </div>

          <div style={fieldStyle}>
            <span style={labelStyle}>
              {t('report.shot.attach', { max: ATTACHMENT_MAX_COUNT })}
              {/* 幾張 / 上限:滿了才知道為什麼按鈕不見,不用去猜。 */}
              <span style={shotCountStyle} data-testid="report-shot-count">
                {shots.length}/{ATTACHMENT_MAX_COUNT}
              </span>
            </span>
            {!shotsFull && (
              <>
                <div style={shotActionsStyle}>
                  <button
                    type="button"
                    onClick={() => void handleCaptureScreen()}
                    style={chipOnStyle}
                    data-testid="report-shot-capture"
                  >
                    {t('report.shot.capture')}
                  </button>
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    style={chipStyle}
                    data-testid="report-shot-pick"
                  >
                    {t('report.shot.pickFile')}
                  </button>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    multiple
                    hidden
                    onChange={(e) => {
                      const picked = e.target.files ? Array.from(e.target.files) : [];
                      e.target.value = '';
                      if (picked.length > 0) void handlePickFiles(picked);
                    }}
                    data-testid="report-shot-file"
                  />
                </div>
                <p style={hintStyle}>
                  {t('report.shot.captureHint')} {t('report.shot.pasteHint')}
                </p>
              </>
            )}
            {/* 滿 3 張時把上限講出來(FB-20260820-0008)—— 按鈕直接消失而不說原因,
                會被讀成「壞掉了」。 */}
            {shotsFull && (
              <p style={hintStyle} data-testid="report-shot-full">
                {t('report.shot.full', { count: ATTACHMENT_MAX_COUNT })}
              </p>
            )}
            {shotState !== 'idle' && (
              <p style={warnStyle} data-testid="report-shot-failed">
                {shotState === 'unsupported' ? t('report.shot.unsupported') : t('report.shot.failed')}
              </p>
            )}
            {shots.map((d, i) => (
              <div key={i} style={shotItemStyle} data-testid={`report-shot-item-${i}`}>
                <ScreenshotAnnotator
                  shot={d.shot}
                  annotations={d.annotations}
                  onChange={(next) => setAnnotationsAt(i, next)}
                />
                <button
                  type="button"
                  onClick={() => removeShotAt(i)}
                  style={previewToggleStyle}
                  data-testid={`report-shot-remove-${i}`}
                >
                  {t('report.shot.removeNth', { n: i + 1 })}
                </button>
              </div>
            ))}
          </div>

          {trailEmpty && (
            <p style={warnStyle} data-testid="report-trail-empty">
              {t('report.trailEmpty')}
            </p>
          )}

          <button
            type="button"
            onClick={() => setShowPreview((v) => !v)}
            style={previewToggleStyle}
            aria-expanded={showPreview}
            data-testid="report-preview-toggle"
          >
            {showPreview ? t('report.previewHide') : t('report.previewToggle')}
          </button>
          {showPreview && (
            <pre ref={preRef} style={preStyle} data-testid="report-preview">
              {text}
            </pre>
          )}

          <p style={hintStyle}>{t('report.hint')}</p>
        </div>

        <div style={footStyle}>
          {copyState === 'failed' && <span style={failedStyle}>{t('report.copyFailed')}</span>}
          {submitState === 'sent' && sentId && (
            <span style={sentStyle} data-testid="report-sent">
              {/* ⚠️ 部分成功要說出來(docs/94 §2.5):BE 先存文字再上傳圖片,所以會出現
                  「已收到但有圖沒上去」。顯示成完全失敗 → 使用者會重送一次;顯示成完全
                  成功 → 他以為圖附上了。兩種都不對。 */}
              {failedAttachments > 0
                ? t('report.submittedPartial', { id: sentId, count: failedAttachments })
                : t('report.submitted', { id: sentId })}
            </span>
          )}
          {submitState === 'failed' && (
            <span style={failedStyle} data-testid="report-submit-failed">
              {submitFailure?.retryable
                ? t(`report.submitError.${submitFailure.reason}` as 'report.submitError.generic')
                : t('report.submitFailed')}
            </span>
          )}
          {/* R265 — 送出成功後多一顆「查看我的回報」。少了它,「可以看到處理進度」對
              一般使用者形同虛設(進入點只有個人資料頁的右鍵選單)。
              ⚠️ 用 `location.assign` 而不是 `useNavigate`:本對話框掛在
              [providers](../app/providers.tsx) 的 `RouterProvider` **外圈**(那是刻意的
              —— 白畫面時 boundary 也要 render 得出它),拿不到 router context。
              整頁載入在這裡可接受:回報剛送完,沒有任何未存狀態會掉。 */}
          {submitState === 'sent' && (
            <button
              type="button"
              onClick={() => window.location.assign('/feedback')}
              style={secondaryBtnStyle}
              data-testid="report-view-mine"
            >
              {t('report.viewMine')}
            </button>
          )}
          {/* 送出成功後這顆改成「複製**編號**」—— 那才是使用者接下來要用的東西
              (診斷碼是送不出去時的退路,已經送進系統就沒必要再貼一長串)。 */}
          <button
            type="button"
            onClick={() => void (submitState === 'sent' ? handleCopySerial() : handleCopy())}
            style={submitState === 'sent' || !SUPPORT_REPORT_SUPPORTED ? primaryBtnStyle : secondaryBtnStyle}
            data-testid={submitState === 'sent' ? 'report-copy-serial' : 'report-copy'}
          >
            {submitState === 'sent'
              ? copyState === 'done'
                ? t('report.serialCopied')
                : t('report.copySerial')
              : copyState === 'done'
                ? t('report.copied')
                : t('report.copy')}
          </button>
          {/* ⏳ BE 端點同步開發中。gate 關閉時**完全不顯示**這顆鈕,而不是畫一顆停用的:
              「複製診斷碼」已是完整可用路徑,一顆灰鈕只會讓人以為功能沒做完。 */}
          {SUPPORT_REPORT_SUPPORTED && submitState !== 'sent' && (
            <button
              type="button"
              onClick={() => void handleSubmit()}
              disabled={submitState === 'sending'}
              style={submitState === 'sending' ? primaryBtnBusyStyle : primaryBtnStyle}
              data-testid="report-submit"
            >
              {submitState === 'sending' ? t('report.submitting') : t('report.submit')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const scrimStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'var(--scrim)',
  zIndex: 240,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 24,
};
const backdropBtnStyle: CSSProperties = {
  position: 'absolute',
  inset: 0,
  border: 'none',
  background: 'transparent',
  cursor: 'default',
  padding: 0,
};
const modalStyle: CSSProperties = {
  position: 'relative',
  width: 'min(520px, 100%)',
  maxHeight: '88vh',
  background: 'var(--ink-900)',
  borderRadius: 'var(--r-lg)',
  boxShadow: 'var(--shadow-lg)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};
const headStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '16px 18px',
  borderBottom: '1px solid var(--hairline)',
  flexShrink: 0,
};
const titleStyle: CSSProperties = {
  fontSize: 'var(--text-md)',
  fontWeight: 700,
  color: 'var(--text-strong)',
};
const closeBtnStyle: CSSProperties = {
  border: 'none',
  background: 'transparent',
  color: 'var(--text-muted)',
  cursor: 'pointer',
  padding: 4,
  display: 'inline-flex',
  borderRadius: 8,
};
const bodyStyle: CSSProperties = {
  padding: '16px 18px',
  display: 'flex',
  flexDirection: 'column',
  gap: 14,
  overflowY: 'auto',
};
const introStyle: CSSProperties = {
  margin: 0,
  fontSize: 13,
  lineHeight: 1.6,
  color: 'var(--text-secondary)',
};
const fieldStyle: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 7 };
const labelStyle: CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--text-primary)',
};
const chipRowStyle: CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: 7 };
const chipStyle: CSSProperties = {
  height: 30,
  padding: '0 12px',
  borderRadius: 8,
  border: '1px solid var(--hairline-strong)',
  background: 'var(--surface-pop)',
  color: 'var(--text-primary)',
  fontSize: 12,
  cursor: 'pointer',
};
/** 選取態(CIS):teal-600 框 + teal-50 底 + teal-700 字。 */
const chipOnStyle: CSSProperties = {
  ...chipStyle,
  borderColor: 'var(--tide-600)',
  background: 'var(--concept-soft)',
  color: 'var(--concept-fg)',
  fontWeight: 700,
};
const textareaStyle: CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '9px 11px',
  borderRadius: 'var(--r-md)',
  border: '1px solid var(--hairline-strong)',
  background: 'var(--ink-950)',
  color: 'var(--text-primary)',
  fontSize: 13,
  fontFamily: 'inherit',
  resize: 'vertical',
};
const warnStyle: CSSProperties = {
  margin: 0,
  fontSize: 12,
  lineHeight: 1.6,
  color: 'var(--text-secondary)',
};
const previewToggleStyle: CSSProperties = {
  alignSelf: 'flex-start',
  border: 'none',
  background: 'transparent',
  color: 'var(--concept-fg)',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
  padding: 0,
  textDecoration: 'underline',
};
const preStyle: CSSProperties = {
  margin: 0,
  maxHeight: 260,
  overflow: 'auto',
  padding: 11,
  borderRadius: 'var(--r-md)',
  border: '1px solid var(--hairline)',
  background: 'var(--ink-950)',
  color: 'var(--text-primary)',
  fontSize: 11,
  lineHeight: 1.55,
  // 診斷是對齊過的表格 —— 一定要等寬字,不然欄位對不上就失去可讀性。
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  whiteSpace: 'pre',
};
const hintStyle: CSSProperties = {
  margin: 0,
  fontSize: 12,
  lineHeight: 1.6,
  color: 'var(--text-secondary)',
};
const footStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: 10,
  padding: '13px 18px',
  borderTop: '1px solid var(--hairline)',
  flexShrink: 0,
};
/** 失敗訊息 = rose(CIS 錯誤色;R266 `--err` 只能當淡底,當字只有 2.7:1)。 */
const failedStyle: CSSProperties = { fontSize: 12, color: 'var(--rose-700)', marginRight: 'auto' };
const sentStyle: CSSProperties = {
  fontSize: 12,
  color: 'var(--concept-fg)',
  fontWeight: 600,
  marginRight: 'auto',
};
const shotActionsStyle: CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: 7 };
/** 「已附 N/3」計數(FB-20260820-0008)。 */
const shotCountStyle: CSSProperties = { marginLeft: 6, color: 'var(--ink-500)', fontWeight: 400 };
/** 每張截圖一塊(標註區 + 移除鈕);多張時彼此要看得出分界。 */
const shotItemStyle: CSSProperties = { marginTop: 8 };
const secondaryBtnStyle: CSSProperties = {
  height: 36,
  padding: '0 16px',
  borderRadius: 10,
  border: '1px solid var(--hairline-strong)',
  background: 'transparent',
  color: 'var(--text-primary)',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
};
/** 實心主行動(CIS `.btn-solid`:#0F766E 底;主色 #0D9488 配白字只有 3.74:1)。 */
const primaryBtnStyle: CSSProperties = {
  height: 36,
  padding: '0 18px',
  borderRadius: 10,
  border: 'none',
  background: 'var(--btn-solid)',
  color: 'var(--on-accent)',
  fontSize: 13,
  fontWeight: 700,
  cursor: 'pointer',
};
/** 送出中(disabled)— CIS 停用態:灰底 + 次文字,不用 opacity 調淡。 */
const primaryBtnBusyStyle: CSSProperties = {
  ...primaryBtnStyle,
  background: 'var(--btn-disabled-bg)',
  color: 'var(--btn-disabled-fg)',
  cursor: 'not-allowed',
};
